import { graphqlRequest } from "./client";
import {
  AI_FIELDS,
  CHECK_FIELDS,
  GATE_FIELDS,
  PLATFORM_FIELDS,
  PROFILE_FIELDS,
  type AIFeedback,
  type CheckItem,
  type Platform,
  type PlatformProfile,
  type StepGate,
} from "./types";

export interface AuditQuestion {
  key: string;
  q: string;
  free: boolean;
  options: { v: string; t: string }[];
}

export interface AuditResult {
  score?: number | null;
  feedback?: AIFeedback | null;
  takenAt?: string | null;
  answers?: string | null;
}

export interface PositionView {
  niche?: string | null;
  nicheChecks: CheckItem[];
  nicheFeedback?: AIFeedback | null;
  audit: AuditResult;
  profiles: PlatformProfile[];
  gate: StepGate;
  done: boolean;
}

export interface ProfileVerification {
  profile: PlatformProfile;
  checks: CheckItem[];
  keptPrevious: boolean;
}

const AUDIT_FIELDS = `score takenAt answers feedback { ${AI_FIELDS} }`;
const POSITION_FIELDS = `niche nicheChecks { ${CHECK_FIELDS} } nicheFeedback { ${AI_FIELDS} } audit { ${AUDIT_FIELDS} } profiles { ${PROFILE_FIELDS} } ${GATE_FIELDS} done`;

/** Active partner platforms. */
export async function fetchPlatforms(): Promise<Platform[]> {
  return (
    await graphqlRequest<{ getPlatforms: Platform[] }>(`{ getPlatforms { ${PLATFORM_FIELDS} } }`)
  ).getPlatforms;
}

/** Step 1: Position. */
export async function fetchPosition(): Promise<PositionView> {
  return (
    await graphqlRequest<{ getPosition: PositionView }>(`{ getPosition { ${POSITION_FIELDS} } }`)
  ).getPosition;
}

/** The arrival Readiness Audit questions. */
export async function fetchAuditQuestions(): Promise<AuditQuestion[]> {
  return (
    await graphqlRequest<{ getAuditQuestions: AuditQuestion[] }>(
      `{ getAuditQuestions { key q free options { v t } } }`,
    )
  ).getAuditQuestions;
}

/** Submit the Readiness Audit. */
export async function submitAudit(
  answers: { key: string; answer: string }[],
): Promise<AuditResult> {
  return (
    await graphqlRequest<{ submitAudit: AuditResult }>(
      `mutation($input: SubmitAuditInput!) { submitAudit(input: $input) { ${AUDIT_FIELDS} } }`,
      { variables: { input: { answers } } },
    )
  ).submitAudit;
}

/** Save the niche line. */
export async function saveNiche(niche: string): Promise<PositionView> {
  return (
    await graphqlRequest<{ saveNiche: PositionView }>(
      `mutation($niche: String!) { saveNiche(niche: $niche) { ${POSITION_FIELDS} } }`,
      { variables: { niche } },
    )
  ).saveNiche;
}

/** AI coaching on the niche line. */
export async function reviewNiche(): Promise<PositionView> {
  return (
    await graphqlRequest<{ reviewNiche: PositionView }>(
      `mutation { reviewNiche { ${POSITION_FIELDS} } }`,
    )
  ).reviewNiche;
}

/** Record that a referral link was opened. */
export async function markPlatformVisited(platformId: string): Promise<void> {
  await graphqlRequest(`mutation($id: String!) { markPlatformVisited(platformId: $id) { id } }`, {
    variables: { id: platformId },
  });
}

/** Verify a platform profile link. */
export async function verifyProfile(platformId: string, url: string): Promise<ProfileVerification> {
  return (
    await graphqlRequest<{ verifyProfile: ProfileVerification }>(
      `mutation($input: VerifyProfileInput!) { verifyProfile(input: $input) { keptPrevious checks { ${CHECK_FIELDS} } profile { ${PROFILE_FIELDS} } } }`,
      { variables: { input: { platformId, url } } },
    )
  ).verifyProfile;
}

/** Complete Step 1. */
export async function completePosition(): Promise<PositionView> {
  return (
    await graphqlRequest<{ completePosition: PositionView }>(
      `mutation { completePosition { ${POSITION_FIELDS} } }`,
    )
  ).completePosition;
}
